/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import { useState } from 'react';

import { Link } from '@coursera/cds-core';

import type { NavigationLink } from 'bundles/unified-seo-content-common/types';

type Props = {
  activeCategorySlug: string;
  label: string;
  href: string;
  slug: string;
  onClick?: () => void;
  subNavigationLinks?: NavigationLink[];
};

const styles = {
  navigationItem: (activeBackground: SerializedStyles) => css`
    width: 100%;
    border-radius: 2px;
    padding: var(--cds-spacing-100) var(--cds-spacing-150) var(--cds-spacing-100) var(--cds-spacing-300);

    &:hover {
      ${activeBackground}
    }
  `,
  styleForUnorderedListTag: css`
    list-style: none;
    margin: 0;
    padding-left: 0;
  `,
  styleForATag: css`
    display: inline-block;
    padding: var(--cds-spacing-50);
    text-decoration: none;
    width: 100%;

    &:hover,
    :active,
    :focus {
      text-decoration: none;
    }
  `,
  styleForSpanTag: css`
    color: var(--cds-color-neutral-primary);
    padding: var(--cds-spacing-50) 0;
    display: inline-block;

    &:hover {
      color: var(--cds-color-feedback-informative);
    }
  `,
  activeNavigationItem: (activeBackground: SerializedStyles) => css`
    ${activeBackground}
  `,
  activeNavigationLink: css`
    color: var(--cds-color-feedback-informative);
  `,
  subNavigationLinks: css`
    padding-left: var(--cds-spacing-150);
  `,
};

const NavigationItem = ({ activeCategorySlug, label, href, slug, onClick, subNavigationLinks }: Props) => {
  const [subNavigationToggle, setSubNavigationToggle] = useState(false);
  const isActiveSubNavigation = subNavigationLinks?.some(
    ({ slug: subNavigationSlug }) => activeCategorySlug === subNavigationSlug
  );
  const isActivePrimaryNavigation = activeCategorySlug === slug;
  const isActiveNavigation = isActiveSubNavigation || isActivePrimaryNavigation;
  const shouldShowSubNavigation = (isActiveNavigation || subNavigationToggle) && !!subNavigationLinks;

  const activeNavigation = css`
    background: linear-gradient(
      to right,
      var(--cds-color-feedback-informative) 0%,
      var(--cds-color-feedback-informative) 4px,
      var(--cds-color-neutral-background-primary-weak) 4px,
      var(--cds-color-neutral-background-primary-weak) 90%
    );
  `;

  const dropdownButtonKeyboardHandler = (ev: React.KeyboardEvent<HTMLDivElement>) => {
    switch (ev.key) {
      case ' ':
      case 'ArrowDown':
      case 'Down':
        setSubNavigationToggle(true);
        break;
      case 'Esc':
      case 'Escape':
        setSubNavigationToggle(false);
        break;
      default:
        break;
    }
  };

  return (
    <div
      css={[
        styles.navigationItem(activeNavigation),
        isActiveNavigation && styles.activeNavigationItem(activeNavigation),
      ]}
      onMouseEnter={() => setSubNavigationToggle(true)}
      onMouseLeave={() => setSubNavigationToggle(false)}
      onTouchStart={() => setSubNavigationToggle(true)}
      onTouchEnd={() => setSubNavigationToggle(false)}
      onKeyDown={dropdownButtonKeyboardHandler}
    >
      <Link
        href={href}
        variant="quiet"
        typographyVariant="bodyPrimary"
        aria-current={isActivePrimaryNavigation ? 'page' : false}
        aria-haspopup={subNavigationLinks && subNavigationLinks.length > 0 ? true : undefined}
        aria-expanded={shouldShowSubNavigation}
        onClick={onClick}
        css={styles.styleForATag}
      >
        <span css={[styles.styleForSpanTag, isActivePrimaryNavigation && styles.activeNavigationLink]}>{label}</span>
      </Link>
      {shouldShowSubNavigation && (
        <ul css={[styles.styleForUnorderedListTag, styles.subNavigationLinks]}>
          {subNavigationLinks.map(({ label: subNavigationLabel, href: subNavigationHref, slug: subNavigationSlug }) => (
            <li key={subNavigationSlug}>
              <Link
                href={subNavigationHref}
                variant="quiet"
                typographyVariant="bodySecondary"
                aria-current={activeCategorySlug === subNavigationSlug ? 'page' : false}
                onClick={onClick}
                css={styles.styleForATag}
              >
                <span
                  css={[
                    styles.styleForSpanTag,
                    activeCategorySlug === subNavigationSlug && styles.activeNavigationLink,
                  ]}
                >
                  {subNavigationLabel}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default NavigationItem;
